import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  ActivityIndicator,
  Text,
  TouchableOpacity,
  ScrollView,
  Modal,
  Pressable,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import ProfessionalCandlestickChart from '@/components/charts/ProfessionalCandlestickChart';
import { polygonChartDataService } from '@/services/api/polygonChartData.service';
import { ChartTimeframe, TIMEFRAME_OPTIONS, DEFAULT_TIMEFRAME } from '@/constants/chartTimeframes';
import {
  ChartResolution,
  RESOLUTION_OPTIONS,
  getDefaultResolutionForTimeframe,
} from '@/constants/chartResolutions';
import { StockChartData } from '@/types/stock.types';
import { useNavigation } from '@react-navigation/native';

interface ChartTabProps {
  ticker: string;
  groupId?: string;
}

export const ChartTab: React.FC<ChartTabProps> = ({ ticker, groupId }) => {
  const [timeframe, setTimeframe] = useState<ChartTimeframe>(DEFAULT_TIMEFRAME);
  const [resolution, setResolution] = useState<ChartResolution>(
    getDefaultResolutionForTimeframe(DEFAULT_TIMEFRAME)
  );
  const [chartData, setChartData] = useState<StockChartData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigation = useNavigation<any>();
  const [tfPickerOpen, setTfPickerOpen] = useState(false);
  const [resPickerOpen, setResPickerOpen] = useState(false);

  const timeframeLabel = (tf: ChartTimeframe) => {
    switch (tf) {
      case '1D':
        return '1 Day';
      case '1W':
        return '1 Week';
      case '1M':
        return '1 Month';
      case '3M':
        return '3 Months';
      case '1Y':
        return '1 Year';
      case '5Y':
        return '5 Years';
      default:
        return tf;
    }
  };

  const resolutionLabel = (r: ChartResolution) => {
    switch (r) {
      case '5m':
        return '5 min';
      case '15m':
        return '15 min';
      case '1h':
        return '1 hour';
      case '4h':
        return '4 hours';
      case '1D':
        return '1 Day';
      case '1W':
        return '1 Week';
      default:
        return r;
    }
  };

  // Load chart data when ticker or timeframe changes
  useEffect(() => {
    const abort = new AbortController();
    const loadChartData = async () => {
      setIsLoading(true);
      setError(null);

      console.log(`📊 [ChartTab] Loading ${timeframe} data for ${ticker}`);

      const { data, error: chartError } = await polygonChartDataService.getChartData(
        ticker,
        timeframe,
        { resolution }
      );

      if (abort.signal.aborted) return;

      if (chartError) {
        console.error('❌ [ChartTab] Failed to load chart data:', chartError);
        setError(chartError);
        setChartData([]);
      } else {
        console.log(`✅ [ChartTab] Loaded ${data.length} data points`);
        setChartData(data);
      }

      setIsLoading(false);
    };

    loadChartData();
    return () => abort.abort();
  }, [ticker, timeframe, resolution]);

  // When timeframe changes, reset resolution to a sensible default for that range
  useEffect(() => {
    setResolution(getDefaultResolutionForTimeframe(timeframe));
  }, [timeframe]);

  return (
    <View style={styles.container}>
      {/* Controls: Range & Interval dropdowns */}
      <View style={styles.controlsRow}>
        <Pressable style={styles.dropdown} onPress={() => setTfPickerOpen(true)}>
          <Ionicons name="calendar" size={16} color={Colors.text.secondary} />
          <Text style={styles.dropdownText}>Range: {timeframeLabel(timeframe)}</Text>
          <Ionicons name="chevron-down" size={16} color={Colors.text.secondary} />
        </Pressable>

        <Pressable style={styles.dropdown} onPress={() => setResPickerOpen(true)}>
          <Ionicons name="time" size={16} color={Colors.text.secondary} />
          <Text style={styles.dropdownText}>Interval: {resolutionLabel(resolution)}</Text>
          <Ionicons name="chevron-down" size={16} color={Colors.text.secondary} />
        </Pressable>
      </View>

      {/* Timeframe Picker Modal */}
      <Modal
        visible={tfPickerOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setTfPickerOpen(false)}
      >
        <Pressable style={styles.backdrop} onPress={() => setTfPickerOpen(false)} />
        <View style={styles.pickerCard}>
          <Text style={styles.pickerTitle}>Select Range</Text>
          {TIMEFRAME_OPTIONS.map(tf => (
            <Pressable
              key={tf}
              onPress={() => {
                setTimeframe(tf);
                setTfPickerOpen(false);
              }}
              style={[styles.pickerItem, timeframe === tf && styles.pickerItemActive]}
            >
              <Text
                style={[styles.pickerItemText, timeframe === tf && styles.pickerItemTextActive]}
              >
                {timeframeLabel(tf)}
              </Text>
            </Pressable>
          ))}
        </View>
      </Modal>

      {/* Resolution Picker Modal */}
      <Modal
        visible={resPickerOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setResPickerOpen(false)}
      >
        <Pressable style={styles.backdrop} onPress={() => setResPickerOpen(false)} />
        <View style={styles.pickerCard}>
          <Text style={styles.pickerTitle}>Select Interval</Text>
          {RESOLUTION_OPTIONS.map(res => (
            <Pressable
              key={res}
              onPress={() => {
                setResolution(res);
                setResPickerOpen(false);
              }}
              style={[styles.pickerItem, resolution === res && styles.pickerItemActive]}
            >
              <Text
                style={[styles.pickerItemText, resolution === res && styles.pickerItemTextActive]}
              >
                {resolutionLabel(res)}
              </Text>
            </Pressable>
          ))}
        </View>
      </Modal>

      {/* Chart Error */}
      {error && (
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle" size={32} color={Colors.status.error} />
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={() => setTimeframe(timeframe)}>
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Chart */}
      {!error && (
        <ProfessionalCandlestickChart
          symbol={ticker}
          chartData={chartData}
          isLoading={isLoading}
          timeframe={timeframe}
          resolution={resolution}
        />
      )}

      {/* Advanced (fullscreen) collaborative chart */}
      <View style={styles.ctaContainer}>
        <TouchableOpacity
          style={styles.ctaButton}
          activeOpacity={0.8}
          onPress={() =>
            navigation.navigate('CollaborativeChart', {
              groupId: groupId || 'solo',
              symbol: ticker,
            })
          }
        >
          <Ionicons name="expand-outline" size={16} color={Colors.text.primary} />
          <Text style={styles.ctaText}>Expand for advanced drawings + indicators</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  controlsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
    gap: 10,
  },
  dropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: Colors.background.secondary,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
    flex: 1,
  },
  dropdownText: {
    flex: 1,
    color: Colors.text.primary,
    fontSize: 13,
    fontWeight: '600',
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
  pickerCard: {
    position: 'absolute',
    left: 12,
    right: 12,
    top: 100,
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
    padding: 12,
  },
  pickerTitle: {
    color: Colors.text.secondary,
    fontSize: 12,
    marginBottom: 8,
  },
  pickerItem: {
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.background.tertiary,
    marginBottom: 8,
  },
  pickerItemActive: {
    backgroundColor: Colors.accent.primary + '10',
    borderColor: Colors.accent.primary,
  },
  pickerItemText: {
    color: Colors.text.primary,
    fontSize: 14,
    fontWeight: '600',
  },
  pickerItemTextActive: {
    color: Colors.accent.primary,
  },
  ctaContainer: {
    paddingHorizontal: 16,
    paddingTop: 12,
  },
  ctaButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: Colors.accent.primary + '20',
    borderWidth: 1,
    borderColor: Colors.accent.primary,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  ctaText: {
    color: Colors.text.primary,
    fontSize: 13,
    fontWeight: '600',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  errorText: {
    marginTop: 16,
    fontSize: 14,
    color: Colors.text.secondary,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 16,
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: Colors.accent.primary,
    borderRadius: 8,
  },
  retryButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.background.primary,
  },
});
